import type { InvitationRole } from "./types";

export interface LinkContext {
  invitationId: string | null;
  roleHint: InvitationRole | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Reads only pathname + search — the two parts of the URL that DO get
// sent in the initial HTTP request to the static host. Never touches
// location.hash; see readFragmentSecret() below for that, kept in a
// deliberately separate function so nothing that also builds a network
// request can accidentally receive the fragment value by sharing a
// parameter list with it.
export function parseLinkContext(pathname: string, search: string): LinkContext {
  const raw = pathname.replace(/^\/+/, "").replace(/\/+$/, "");
  const invitationId = raw && UUID_RE.test(raw) ? raw : null;

  const params = new URLSearchParams(search);
  const roleParam = params.get("role");
  // An unrecognized/missing role is not an error — it's just ignored.
  // The authoritative role always comes from preview_invitation_public()
  // once it responds; this hint only affects what the loading state
  // could optimistically show, and currently isn't even used for that.
  const roleHint: InvitationRole | null =
    roleParam === "caregiver" || roleParam === "nurse" ? roleParam : null;

  return { invitationId, roleHint };
}

// The ONLY function in this app that reads location.hash. Its result
// must never be passed to fetch(), console.*, or any analytics/logging
// call — see supabaseClient.ts (never receives it) and render.ts (only
// use: a same-origin app-link href and a clipboard-copy button).
export function readFragmentSecret(hash: string): string | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  return params.get("secret");
}
